import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowRight, Play } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import InterestModal from "./InterestModal";

const translations = {
  overline: {
    es: "7 programas avalados por PCMA · Formación B2B especializada",
    en: "7 programs endorsed by PCMA · Specialized B2B training",
  },
  headline: {
    es: "Formamos a los equipos que impulsan la",
    en: "We train the teams that drive the",
  },
  headlineHighlight: {
    es: "Industria de Reuniones",
    en: "Meetings Industry",
  },
  headlineEnd: {
    es: "en Latinoamérica",
    en: "in Latin America",
  },
  subheadline: {
    es: "Diseñamos programas formativos a medida para Convention Bureaus, destinos, cadenas hoteleras, DMCs, recintos y corporativos que quieren profesionalizar sus equipos y competir a nivel global.",
    en: "We design tailored training programs for Convention Bureaus, destinations, hotel chains, DMCs, venues, and corporates aiming to professionalize their teams and compete globally.",
  },
  ctaPrimary: {
    es: "Solicitar propuesta",
    en: "Request a proposal",
  },
  ctaSecondary: {
    es: "Conocer la academia",
    en: "About the Academy",
  },
  trustText: {
    es: "Respaldados por líderes de la industria",
    en: "Backed by industry leaders",
  },
  experience: {
    es: "+20 años de experiencia",
    en: "+20 years of experience",
  },
};

const HeroSection = () => {
  const { t } = useLanguage();
  const [interestOpen, setInterestOpen] = useState(false);

  const scrollToAbout = () => {
    document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1561489396-888724a1543d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbmZlcmVuY2V8ZW58MHx8fHwxNzc1NzU1MTExfDA&ixlib=rb-4.1.0&q=85"
          alt="Conferencia MICE — industria de reuniones en Latinoamérica"
          width="1920"
          height="1080"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3B]/95 via-[#0B1F3B]/85 to-[#0B1F3B]" />
      </div>

      {/* Animated gradient orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#2ED3C6]/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-[#F59E0B]/15 rounded-full blur-3xl"
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        <div className="max-w-4xl">
          {/* Overline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 text-[#2ED3C6] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body">
              <span className="w-8 h-[2px] bg-[#2ED3C6]" />
              {t(translations.overline)}
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="hero-headline"
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white font-semibold tracking-tight leading-none mb-8"
          >
            {t(translations.headline)}{" "}
            <span className="text-gradient">{t(translations.headlineHighlight)}</span>{" "}
            {t(translations.headlineEnd)}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            data-testid="hero-subheadline"
            className="text-white/70 text-lg md:text-xl lg:text-2xl font-body font-light max-w-2xl mb-12 leading-relaxed"
          >
            {t(translations.subheadline)}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => setInterestOpen(true)}
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-3 bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-4 text-lg transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-[#F59E0B]/30 group"
            >
              {t(translations.ctaPrimary)}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={scrollToAbout}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border-2 border-white/30 hover:border-[#2ED3C6] text-white font-medium px-8 py-4 text-lg transition-all hover:bg-white/5"
            >
              <Play className="w-5 h-5" />
              {t(translations.ctaSecondary)}
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <p className="text-white/40 text-sm font-body mb-4">{t(translations.trustText)}</p>
            <div className="flex items-center gap-8 flex-wrap">
              <a href="#pcma" className="text-white/60 hover:text-[#2ED3C6] font-display text-lg transition-colors">PCMA Institute</a>
              <div className="text-white/60 font-display text-lg">Incubatour</div>
              <div className="text-white/60 font-display text-lg">{t(translations.experience)}</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-[#2ED3C6] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
    <InterestModal open={interestOpen} onClose={() => setInterestOpen(false)} />
    </>
  );
};

export default HeroSection;
